import * as React from "react";
import {
    isNavigationKey,
    getCellProperty,
    isAlphaNumericKey,
    keyCodes,
    isNumpadNumericKey,
} from "@silevis/reactgrid";
import { tryParseFormula } from "./formulaParser";
import { formattedCurrency, formattedPercentage } from "@/utils";
import HighlightCell from "./HighlightCell";
import ContextMenuCellWrapper from "./ContextMenuCellWrapper";

export class FormulaCellTemplate {
    constructor(oneAsEmpty = false, isPercentage = false) {
        this.oneAsEmpty = oneAsEmpty;
        this.isPercentage = isPercentage;
    }

    getCompatibleCell(uncertainCell) {
        const text = uncertainCell.text;
        const value = parseFloat(
            getCellProperty(uncertainCell, "value", "number"),
        );

        return { ...uncertainCell, text, value };
    }

    getStyle() {
        return {
            overflow: "visible",
        };
    }

    handleKeyDown(cell, keyCode, ctrl, shift, alt) {
        if (
            !ctrl &&
            !alt &&
            (isAlphaNumericKey(keyCode) || isNumpadNumericKey(keyCode))
        ) {
            const keyMap = {
                187: "=",
                189: "-",
            };
            const char = isNumpadNumericKey(keyCode)
                ? String.fromCharCode(keyCode - 48)
                : String.fromCharCode(keyCode);
            if (/^\d$/.test(char)) {
                const updatedCell = this.update(cell, {
                    ...cell,
                    text: parseFloat(char),
                    value: parseFloat(char),
                });
                return {
                    cell: {
                        ...updatedCell,
                        value: parseValue(
                            updatedCell.value,
                            this.oneAsEmpty,
                            this.isPercentage,
                        ),
                    },
                    enableEditMode: true,
                };
            } else if (keyCode == 187 || keyCode == 189) {
                return {
                    cell: { ...cell, value: keyMap[keyCode] },
                    enableEditMode: true,
                };
            }
        }

        return {
            cell: { ...cell, text: cell.text, value: cell.value },
            enableEditMode:
                keyCode === keyCodes.POINTER || keyCode === keyCodes.ENTER,
        };
    }

    update(cell, cellToMerge) {
        const parsedVal = parseValue(cellToMerge.value, this.oneAsEmpty);
        return this.getCompatibleCell({
            ...cell,
            text: parsedVal.toString(),
            value: parsedVal,
        });
    }

    render(cell, isInEditMode, onCellChanged = (cell, commit) => {}) {
        return (
            <ContextMenuCellWrapper cell={cell}>
                <FormulaCell
                    isInEditMode={isInEditMode}
                    cell={cell}
                    getCompatibleCell={this.getCompatibleCell}
                    onCellChanged={onCellChanged}
                    oneAsEmpty={this.oneAsEmpty}
                    isPercentage={this.isPercentage}
                />
            </ContextMenuCellWrapper>
        );
    }
}

const FormulaCell = ({
    cell,
    getCompatibleCell,
    onCellChanged,
    isInEditMode,
    oneAsEmpty,
    isPercentage,
}) => {
    const [hover, setHover] = React.useState();
    const handleMouseEnter = () => {
        setHover(true);
    };
    const handleMouseLeave = () => {
        setHover(false);
    };
    return (
        <div
            style={{ width: "100%", height: "100%" }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <div
                style={{
                    height: "100%",
                    justifyContent: "right",
                    alignItems: "center",
                    display: "flex",
                }}
            >
                {isInEditMode ? (
                    <div style={{ display: "flex" }}>
                        <input
                            style={{
                                width: "100%",
                                border: "none",
                                outline: "none",
                            }}
                            ref={(input) => {
                                input && input.focus();
                            }}
                            defaultValue={cell.value}
                            onChange={(e) => {
                                const parsedVal = parseValue(
                                    e.currentTarget.value,
                                    oneAsEmpty,
                                    isPercentage,
                                );
                                onCellChanged(
                                    getCompatibleCell({
                                        ...cell,
                                        text: parsedVal.toString(),
                                        value: parseFloat(parsedVal),
                                    }),
                                    false,
                                );
                            }}
                            onFocus={(e) => {
                                e.currentTarget.value = parseFromDisplayValue(
                                    e.currentTarget.value,
                                    isPercentage,
                                );
                            }}
                            onCopy={(e) => e.stopPropagation()}
                            onCut={(e) => e.stopPropagation()}
                            onPaste={(e) => e.stopPropagation()}
                            onPointerDown={(e) => e.stopPropagation()}
                            onKeyDown={(e) => {
                                if (
                                    isAlphaNumericKey(e.keyCode) ||
                                    isNavigationKey(e.keyCode)
                                )
                                    e.stopPropagation();
                            }}
                            onBlur={(e) => {
                                const parsedVal = parseValue(
                                    e.currentTarget.value,
                                    oneAsEmpty,
                                    isPercentage,
                                );
                                onCellChanged(
                                    getCompatibleCell({
                                        ...cell,
                                        text: parsedVal.toString(),
                                        value: parseFloat(parsedVal),
                                    }),
                                    true,
                                );
                            }}
                        />
                        {isPercentage && (
                            <div
                                style={{
                                    position: "absolute",
                                    right: 0,
                                    top: 0,
                                    padding: 2,
                                }}
                            >
                                <span style={{ fontSize: 12 }}> % </span>
                            </div>
                        )}
                    </div>
                ) : (
                    <div>
                        <span>
                            {displayValue(cell.value, oneAsEmpty, isPercentage)}
                        </span>
                    </div>
                )}
            </div>
            <HighlightCell hover={hover} cell={cell} />
        </div>
    );
};

const displayValue = (value, oneAsEmpty, isPercentage) => {
    if (value == 0) {
        return "";
    }

    const isDecimalWithinPositiveAndNegativeOne =
        value % 1 != 0 && value > -1 && value < 1;

    return isPercentage || isDecimalWithinPositiveAndNegativeOne
        ? formattedPercentage(value, 0)
        : formattedCurrency(value);
};

const parseValue = (value, oneAsEmpty, isPercentage) => {
    const assumeZero = value == "" || Number.isNaN(value);
    let val = assumeZero ? 0 : value;

    let parsedVal;

    if (typeof val == "string" && val.endsWith("%") && !val.startsWith("=")) {
        val = `=${val}`;
    }

    parsedVal = tryParseFormula(val);
    const finalValue = isPercentage
        ? parseFloat((parsedVal / 100).toFixed(2))
        : parsedVal;
    return finalValue;
};

const parseFromDisplayValue = (value, isPercentage) => {
    return isPercentage ? parseFloat((value * 100).toFixed(2)) : value;
};
